import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  CalendarOff,
  Calendar,
  Info,
  Loader2,
  Lock,
  MessageSquare,
  RotateCcw,
  Save,
  Settings,
  Stethoscope,
  Users,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../config/firebase';
import MainLayout from '../components/layout/MainLayout';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useToast } from '../contexts/ToastContext';
import { HorariosEditor } from '../components/admin/HorariosEditor';
import { useCambiosSinGuardar } from '../hooks/useCambiosSinGuardar';
import BadgeCambiosSinGuardar from '../components/ui/BadgeCambiosSinGuardar';
import { SuspensionesEditor } from '../components/admin/SuspensionesEditor';
import { BloqueosRecurrentesEditor } from '../components/admin/BloqueosRecurrentesEditor';
import { MedicosAsignadosEditor } from '../components/admin/MedicosAsignadosEditor';
import { VistaPreviaCapacidad } from '../components/admin/VistaPreviaCapacidad';
import { RoutingPolicyEditor } from '../components/admin/RoutingPolicyEditor';
import type { ModoAsignacion, Sala, TipoSala, PrioridadSala } from '../types';
import { MENSAJES, type MensajeKey } from '../config/mensajesPaciente';
import styles from './SalaFormPage.module.css';

const TIPOS_SALA: { value: TipoSala; label: string }[] = [
  { value: 'general', label: 'Medicina General' },
  { value: 'pediatria', label: 'Pediatría' },
  { value: 'urgencias', label: 'Urgencias' },
  { value: 'especialidades', label: 'Especialidades' },
  { value: 'psicologia', label: 'Psicología' },
  { value: 'ginecologia', label: 'Ginecología' },
  { value: 'dermatologia', label: 'Dermatología' },
  { value: 'otro', label: 'Otro' },
];

const PRIORIDADES: { value: PrioridadSala; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'alta', label: 'Alta' },
  { value: 'urgente', label: 'Urgente' },
];

const MODOS_ASIGNACION: {
  value: ModoAsignacion;
  label: string;
  description: string;
}[] = [
  {
    value: 'manual',
    label: 'Manual',
    description:
      'El médico elige al paciente de la cola. Sin asignación automática.',
  },
  {
    value: 'automatico',
    label: 'Automático',
    description:
      'El sistema asigna el siguiente paciente al terminar cada consulta.',
  },
  {
    value: 'hibrido',
    label: 'Híbrido (recomendado)',
    description:
      'El sistema asigna pero el médico puede aceptar, rechazar o elegir otro paciente.',
  },
];

// DIAS_SEMANA legacy removido — los horarios ahora se editan en la tab
// "Horarios" vía HorariosEditor (modelo V2, subcolección).

/**
 * Atajos rápidos para configurar horarios al crear una sala nueva.
 * Cada uno expande a un set de reglas `{diaSemana, horaInicio, horaFin}`.
 */
type AtajoHorario = 'lun_vie_8_17' | 'lun_sab_7_19' | 'siempre' | 'personalizado';

const ATAJOS_HORARIO: Record<
  Exclude<AtajoHorario, 'personalizado'>,
  { label: string; reglas: { diaSemana: number; horaInicio: string; horaFin: string }[] }
> = {
  lun_vie_8_17: {
    label: 'Lun-Vie 08:00 a 17:00',
    reglas: [1, 2, 3, 4, 5].map((d) => ({
      diaSemana: d,
      horaInicio: '08:00',
      horaFin: '17:00',
    })),
  },
  lun_sab_7_19: {
    label: 'Lun-Sáb 07:00 a 19:00',
    reglas: [1, 2, 3, 4, 5, 6].map((d) => ({
      diaSemana: d,
      horaInicio: '07:00',
      horaFin: '19:00',
    })),
  },
  siempre: {
    label: '24/7 — siempre abierta',
    reglas: [0, 1, 2, 3, 4, 5, 6].map((d) => ({
      diaSemana: d,
      horaInicio: '00:00',
      horaFin: '23:59',
    })),
  },
};

interface SalaForm {
  nombre: string;
  descripcion: string;
  tipo: TipoSala;
  activa: boolean;
  limitePacientes: number;
  tiempoEstimadoMin: number;
  bufferCierreMins: number;
  prioridad: PrioridadSala;
  modoAsignacion: ModoAsignacion;
  /** COLA-02: en modo Híbrido, ofrecer al médico (aceptar/rechazar) en vez de
   *  asignar directo. Opt-in. */
  ofertaHibridaActiva: boolean;
  /** CAP: gate de admisión por capacidad dinámica (médicos en turno). Opt-in. */
  gateDinamico: boolean;
  horizonteCapacidadHoras: number;
  descontarDesconectados: boolean;
  ventanaPresenciaMin: number;
  /** Duración de la sesión de video = tiempo por consulta + este margen. */
  bufferSesionMin: number;
  /** Minutos que tiene el médico para iniciar el video tras tomar al paciente. */
  timeoutPreVideoMin: number;
  /** Segundos de gracia si el médico pierde conexión durante la llamada. */
  graciaReconexionMedicoSeg: number;
  graciaFinalizacionMin: number;
  requiereAprobacion: boolean;
  instrucciones: string;
  /** Cita en HCIS: servicio, centro y prestación que se envían en ConfirmarCitaHcis. */
  seisServicioId: string;
  seisCentroId: string;
  seisPrestacionId: string;
  /** Cita SEIS: minutos a los que se redondea hacia arriba la hora de la cita. */
  seisRedondeoMinutos: number;
  /** Plantillas personalizadas de mensajes de rechazo al paciente. Sólo se
   *  incluyen las claves que el admin editó; el resto usa el default. */
  mensajesPaciente: Partial<Record<MensajeKey, string>>;
  /** Solo se usa al CREAR — preset de horario a aplicar. En modo edit los
   *  horarios se editan via tab "Horarios" con HorariosEditor (subcolección). */
  atajoHorario: AtajoHorario;
}

const DEFAULT_FORM: SalaForm = {
  nombre: '',
  descripcion: '',
  tipo: 'general',
  activa: true,
  limitePacientes: 20,
  tiempoEstimadoMin: 15,
  bufferCierreMins: 15,
  prioridad: 'normal',
  modoAsignacion: 'hibrido',
  ofertaHibridaActiva: false,
  gateDinamico: false,
  horizonteCapacidadHoras: 3,
  descontarDesconectados: false,
  ventanaPresenciaMin: 10,
  bufferSesionMin: 5,
  timeoutPreVideoMin: 5,
  graciaReconexionMedicoSeg: 120,
  graciaFinalizacionMin: 15,
  requiereAprobacion: false,
  instrucciones: '',
  seisServicioId: '',
  seisCentroId: '',
  seisPrestacionId: '',
  seisRedondeoMinutos: 10,
  mensajesPaciente: {},
  atajoHorario: 'lun_vie_8_17',
};

/** Rangos seguros de los parámetros de capacidad (coinciden con el backend
 *  `LIMITES_CAPACIDAD` — el backend recorta igual, aquí es sólo la guía visual). */
const RANGOS = {
  limitePacientes: { min: 1, max: 100000 },
  tiempoEstimadoMin: { min: 1, max: 240 },
  bufferCierreMins: { min: 0, max: 240 },
  horizonteCapacidadHoras: { min: 1, max: 12 },
  ventanaPresenciaMin: { min: 1, max: 120 },
  bufferSesionMin: { min: 0, max: 60 },
  timeoutPreVideoMin: { min: 1, max: 60 },
  graciaReconexionMedicoSeg: { min: 10, max: 600 },
  graciaFinalizacionMin: { min: 3, max: 60 },
  seisRedondeoMinutos: { min: 1, max: 60 },
} as const;

const clamp = (v: number, r: { min: number; max: number }) =>
  Math.min(r.max, Math.max(r.min, Number.isFinite(v) ? v : r.min));

/** Tarjeta de ayuda punto por punto (columna derecha de Capacidad). */
function HelpItem({ n, titulo, children }: { n: number; titulo: string; children: ReactNode }) {
  return (
    <div className={styles.helpItem}>
      <span className={styles.helpItemNum}>{n}</span>
      <div className={styles.helpItemBody}>
        <div className={styles.helpItemTitle}>{titulo}</div>
        <div className={styles.helpItemText}>{children}</div>
      </div>
    </div>
  );
}

function salaToForm(sala: Sala): SalaForm {
  return {
    nombre: sala.nombre,
    descripcion: sala.descripcion,
    tipo: (sala.tipo as TipoSala) ?? 'general',
    activa: sala.activa,
    limitePacientes: sala.limitePacientes ?? 20,
    tiempoEstimadoMin: sala.tiempoEstimadoMin ?? sala.tiempoConsultaMin ?? 15,
    bufferCierreMins: sala.bufferCierreMins ?? 15,
    prioridad: sala.prioridad ?? 'normal',
    modoAsignacion: sala.modoAsignacion ?? 'hibrido',
    ofertaHibridaActiva: sala.ofertaHibridaActiva ?? false,
    gateDinamico: sala.gateDinamico ?? false,
    horizonteCapacidadHoras: sala.horizonteCapacidadHoras ?? 3,
    descontarDesconectados: sala.descontarDesconectados ?? false,
    ventanaPresenciaMin: sala.ventanaPresenciaMin ?? 10,
    bufferSesionMin: sala.bufferSesionMin ?? 5,
    timeoutPreVideoMin: sala.timeoutPreVideoMin ?? 5,
    graciaReconexionMedicoSeg: sala.graciaReconexionMedicoSeg ?? 120,
    graciaFinalizacionMin: sala.graciaFinalizacionMin ?? 15,
    requiereAprobacion: sala.requiereAprobacion ?? false,
    instrucciones: sala.instrucciones ?? '',
    seisServicioId: sala.seisServicioId ?? '',
    seisCentroId: sala.seisCentroId ?? '',
    seisPrestacionId: sala.seisPrestacionId ?? '',
    seisRedondeoMinutos: sala.seisRedondeoMinutos ?? 10,
    mensajesPaciente: { ...(sala.mensajesPaciente ?? {}) },
    atajoHorario: 'personalizado', // en edit modo, no se aplica atajo
  };
}

type TabKey =
  | 'general'
  | 'horarios'
  | 'medicos'
  | 'capacidad'
  | 'mensajes'
  | 'vista-previa';

const TABS: { key: TabKey; label: string; icon: typeof Stethoscope; advanced?: boolean }[] = [
  { key: 'general',      label: 'General',      icon: Stethoscope },
  { key: 'horarios',     label: 'Horarios',     icon: Calendar },
  { key: 'medicos',      label: 'Médicos',      icon: Users,          advanced: true },
  { key: 'capacidad',    label: 'Capacidad',    icon: Settings },
  { key: 'mensajes',     label: 'Mensajes',     icon: MessageSquare,  advanced: true },
  { key: 'vista-previa', label: 'Vista previa', icon: BarChart3,      advanced: true },
];

export default function SalaFormPage() {
  const { salaId } = useParams<{ salaId?: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const isEdit = !!salaId;

  const [form, setForm] = useState<SalaForm>({ ...DEFAULT_FORM });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('general');

  // Aviso de cambios sin guardar — SOLO en edición. Al crear no aplica: no hay
  // línea base contra la cual comparar.
  //
  // Acá va el BADGE y no la barra fija: esta página ya tiene su propia action
  // bar sticky al pie (siempre visible), así que una barra flotante encima
  // quedaba duplicada. Regla: si la pantalla ya tiene una barra de acciones
  // persistente, el aviso la acompaña en vez de reemplazarla.
  const { hayCambios, marcarGuardado } = useCambiosSinGuardar(form, {
    habilitado: isEdit && !loading,
  });

  // Load existing sala if editing
  useEffect(() => {
    if (!salaId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'salas', salaId));
        if (cancelled) return;
        if (!snap.exists()) {
          setError('No se encontró la sala solicitada.');
          return;
        }
        const sala = { id: snap.id, ...snap.data() } as Sala;
        setForm(salaToForm(sala));
      } catch (err) {
        console.error('Error cargando sala:', err);
        if (!cancelled) setError('No se pudo cargar la información de la sala.');
      } finally {
        if (!cancelled) {
          setLoading(false);
          // Línea base: recién acá las ediciones cuentan como pendientes.
          if (isEdit) marcarGuardado();
        }
      }
    })();
    return () => { cancelled = true; };
  }, [salaId]);

  const updateField = <K extends keyof SalaForm>(key: K, value: SalaForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  /** Actualiza una plantilla de mensaje; cadena vacía = usar el default (se omite). */
  const updateMensaje = (key: MensajeKey, value: string) => {
    setForm((prev) => {
      const next = { ...prev.mensajesPaciente };
      if (value.trim() === '') delete next[key];
      else next[key] = value;
      return { ...prev, mensajesPaciente: next };
    });
  };

  const canSubmit = useMemo(() => {
    return form.nombre.trim() !== '' && !saving;
  }, [form, saving]);

  const handleCancel = () => {
    navigate('/config/salas');
  };

  const handleSave = useCallback(async () => {
    if (!form.nombre.trim()) {
      addToast('error', 'El nombre de la sala es obligatorio');
      return;
    }

    setSaving(true);
    const payloadBase = {
      nombre: form.nombre.trim(),
      descripcion: form.descripcion.trim(),
      tipo: form.tipo,
      activa: form.activa,
      limitePacientes: form.limitePacientes,
      tiempoEstimadoMin: form.tiempoEstimadoMin,
      tiempoConsultaDefault: form.tiempoEstimadoMin,
      bufferCierreMins: form.bufferCierreMins,
      prioridad: form.prioridad,
      modoAsignacion: form.modoAsignacion,
      // COLA-02: la oferta sólo aplica en modo Híbrido.
      ofertaHibridaActiva: form.modoAsignacion === 'hibrido' ? form.ofertaHibridaActiva : false,
      // CAP: gate de admisión por capacidad dinámica (opt-in) + parámetros.
      gateDinamico: form.gateDinamico,
      horizonteCapacidadHoras: clamp(form.horizonteCapacidadHoras, RANGOS.horizonteCapacidadHoras),
      descontarDesconectados: form.descontarDesconectados,
      ventanaPresenciaMin: clamp(form.ventanaPresenciaMin, RANGOS.ventanaPresenciaMin),
      // Tiempos de la sesión de teleconsulta.
      bufferSesionMin: clamp(form.bufferSesionMin, RANGOS.bufferSesionMin),
      timeoutPreVideoMin: clamp(form.timeoutPreVideoMin, RANGOS.timeoutPreVideoMin),
      graciaReconexionMedicoSeg: clamp(form.graciaReconexionMedicoSeg, RANGOS.graciaReconexionMedicoSeg),
      graciaFinalizacionMin: clamp(form.graciaFinalizacionMin, RANGOS.graciaFinalizacionMin),
      requiereAprobacion: form.requiereAprobacion,
      instrucciones: form.instrucciones.trim(),
      // Cita en HCIS — códigos del catálogo de HCIS para ConfirmarCitaHcis.
      seisServicioId: form.seisServicioId.trim(),
      seisCentroId: form.seisCentroId.trim(),
      seisPrestacionId: form.seisPrestacionId.trim(),
      seisRedondeoMinutos: clamp(form.seisRedondeoMinutos, RANGOS.seisRedondeoMinutos),
      // Plantillas de mensajes al paciente (sólo las personalizadas). El backend
      // sanea y, si queda vacío, restaura los textos por defecto.
      mensajesPaciente: form.mensajesPaciente,
    };

    try {
      if (isEdit && salaId) {
        const actualizarSala = httpsCallable(functions, 'portal_actualizarSala');
        await actualizarSala({ salaId, ...payloadBase });
        marcarGuardado();
        addToast('success', `Sala "${form.nombre}" actualizada`);
        navigate('/config/salas');
      } else {
        // Crear: opcionalmente embebe reglas iniciales según atajo
        const crearSala = httpsCallable(functions, 'portal_crearSala');
        const reglas =
          form.atajoHorario !== 'personalizado'
            ? ATAJOS_HORARIO[form.atajoHorario].reglas
            : undefined;
        const result = await crearSala({ ...payloadBase, horarios: reglas });
        const data = result.data as { salaId: string; reglasCreadas?: number };
        addToast(
          'success',
          `Sala "${form.nombre}" creada${
            data.reglasCreadas ? ` con ${data.reglasCreadas} bloques horarios` : ''
          }`,
        );
        // Si eligió "personalizado" o no se aplicó atajo, llevarlo a editar la sala
        // para que configure horarios en el editor V2.
        if (form.atajoHorario === 'personalizado') {
          navigate(`/config/salas/${data.salaId}/editar`);
        } else {
          navigate('/config/salas');
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al guardar la sala';
      addToast('error', msg);
    } finally {
      setSaving(false);
    }
  }, [form, isEdit, salaId, addToast, navigate]);

  if (loading) {
    return (
      <MainLayout pageTitle={isEdit ? 'Editar sala' : 'Nueva sala'}>
        <div className={styles.loadingState}>
          <Loader2 size={24} className={styles.spinner} />
          <span>Cargando sala…</span>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout pageTitle="Sala no encontrada">
        <div className={styles.errorState}>
          <p>{error}</p>
          <Button variant="secondary" onClick={() => navigate('/config/salas')}>
            <ArrowLeft size={14} /> Volver a salas
          </Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      pageTitle={isEdit ? 'Editar sala' : 'Nueva sala'}
      pageSubtitle={isEdit
        ? 'Modifique los parámetros de la sala. Los cambios aplican inmediatamente.'
        : 'Configure los parámetros de una nueva sala de atención.'}
      onBack={() => navigate('/config/salas')}
    >
      <div className={styles.page}>
        {/* ── Tab navigation ── */}
        <nav className={styles.tabs} role="tablist" aria-label="Configuración de la sala">
          {TABS.map((t) => {
            const disabled = t.advanced === true && !isEdit;
            const active = tab === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={active}
                disabled={disabled}
                title={disabled ? 'Disponible después de guardar la sala' : t.label}
                className={`${styles.tab} ${active ? styles.tabActive : ''}`}
                onClick={() => !disabled && setTab(t.key)}
              >
                <Icon size={14} aria-hidden="true" />
                <span>{t.label}</span>
                {disabled && <span className={styles.tabLock}>🔒</span>}
              </button>
            );
          })}
        </nav>

        {/* Form sections in cards */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
          className={styles.formStack}
        >
          {/* ── Tab: General ── */}
          {tab === 'general' && (
          <>
          <div className={styles.tabBanner}>
            <Info size={16} aria-hidden="true" />
            <div>
              <strong>Información básica</strong> &mdash; Datos principales que verán
              médicos y pacientes. El nombre y el tipo son obligatorios; la descripción
              ayuda a los pacientes a identificar la sala correcta para su consulta.
            </div>
          </div>
          <section className={styles.card}>
            <header className={styles.cardHead}>
              <div>
                <h2 className={styles.cardTitle}>
                  <Stethoscope size={16} /> Información básica
                </h2>
                <p className={styles.cardSubtitle}>
                  Datos principales que verán médicos y pacientes.
                </p>
              </div>
            </header>
            <div className={styles.cardBody}>
              <div className={styles.formGrid}>
                <div className={styles.fullWidth}>
                  <Input
                    label="Nombre de la sala *"
                    placeholder="Ej. Medicina General — Panamá Metro"
                    value={form.nombre}
                    onChange={(e) => updateField('nombre', e.target.value)}
                    required
                  />
                </div>

                <div className={styles.fullWidth}>
                  <label className={styles.label}>Descripción</label>
                  <textarea
                    className={styles.textarea}
                    value={form.descripcion}
                    onChange={(e) => updateField('descripcion', e.target.value)}
                    placeholder="Breve descripción de la especialidad y servicios."
                    rows={3}
                  />
                </div>

                <div className={styles.selectField}>
                  <label className={styles.label}>Tipo de sala</label>
                  <select
                    className={styles.select}
                    value={form.tipo}
                    onChange={(e) => updateField('tipo', e.target.value as TipoSala)}
                  >
                    {TIPOS_SALA.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.selectField}>
                  <label className={styles.label}>Prioridad</label>
                  <select
                    className={styles.select}
                    value={form.prioridad}
                    onChange={(e) => updateField('prioridad', e.target.value as PrioridadSala)}
                  >
                    {PRIORIDADES.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Cita en HCIS — códigos del catálogo de HCIS para ConfirmarCitaHcis */}
              <div className={styles.formGrid} style={{ marginTop: 12 }}>
                <Input
                  label="Servicio HCIS (codigoServicio)"
                  placeholder="Ej. 832"
                  value={form.seisServicioId}
                  onChange={(e) => updateField('seisServicioId', e.target.value)}
                />
                <Input
                  label="Centro médico HCIS (codigoCentroMedico)"
                  placeholder="Ej. 101"
                  inputMode="numeric"
                  value={form.seisCentroId}
                  onChange={(e) => updateField('seisCentroId', e.target.value.replace(/\D/g, ''))}
                />
                <Input
                  label="Prestación HCIS (codPrestacion)"
                  placeholder="Ej. CO_PRVISUB"
                  value={form.seisPrestacionId}
                  onChange={(e) => updateField('seisPrestacionId', e.target.value)}
                />
                <Input
                  type="number"
                  label="Redondeo de la hora (minutos)"
                  placeholder="10"
                  min={RANGOS.seisRedondeoMinutos.min}
                  max={RANGOS.seisRedondeoMinutos.max}
                  value={form.seisRedondeoMinutos}
                  onChange={(e) => updateField('seisRedondeoMinutos', Number(e.target.value))}
                />
              </div>
              <p style={{ marginTop: 6, fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                Son los códigos del catálogo de HCIS que se usan al confirmar la cita en MINSA
                cuando un médico toma un paciente de esta sala. El cupo que se pide empieza en la
                hora actual redondeada hacia arriba al siguiente múltiplo de este valor (ej. con
                10 min: 8:08 → 8:10, 8:51 → 9:00) y dura ese mismo tiempo.
              </p>
            </div>
          </section>

          </>
          )}

          {/* ── Tab: Horarios ── */}
          {tab === 'horarios' && (
          <>
          {/* En CREAR: muestra atajos rápidos. En EDITAR: muestra editor V2 + suspensiones. */}
          {!isEdit && (
            <>
              <div className={styles.tabBanner}>
                <Info size={16} aria-hidden="true" />
                <div>
                  <strong>Horario inicial</strong> &mdash; Elija un preset para
                  configurar la sala rápidamente. Después de crearla podrá editar
                  bloques individuales, agregar suspensiones y horarios diferentes
                  por día. Si necesita una configuración compleja desde el inicio
                  seleccione <em>Personalizado</em>.
                </div>
              </div>

              <section className={styles.card}>
                <header className={styles.cardHead}>
                  <div>
                    <h2 className={styles.cardTitle}>
                      <Calendar size={16} /> Preset de horario
                    </h2>
                    <p className={styles.cardSubtitle}>
                      Aplica un patrón común con un click.
                    </p>
                  </div>
                </header>
                <div className={styles.cardBody}>
                  <div className={styles.atajos}>
                    {(['lun_vie_8_17', 'lun_sab_7_19', 'siempre'] as const).map((key) => (
                      <label
                        key={key}
                        className={`${styles.atajoOption} ${
                          form.atajoHorario === key ? styles.atajoOptionActive : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="atajo"
                          value={key}
                          checked={form.atajoHorario === key}
                          onChange={() => updateField('atajoHorario', key)}
                        />
                        <div>
                          <div className={styles.atajoLabel}>
                            {ATAJOS_HORARIO[key].label}
                          </div>
                          <div className={styles.atajoCount}>
                            {ATAJOS_HORARIO[key].reglas.length} bloque
                            {ATAJOS_HORARIO[key].reglas.length === 1 ? '' : 's'}
                          </div>
                        </div>
                      </label>
                    ))}
                    <label
                      className={`${styles.atajoOption} ${
                        form.atajoHorario === 'personalizado' ? styles.atajoOptionActive : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="atajo"
                        value="personalizado"
                        checked={form.atajoHorario === 'personalizado'}
                        onChange={() => updateField('atajoHorario', 'personalizado')}
                      />
                      <div>
                        <div className={styles.atajoLabel}>Personalizado</div>
                        <div className={styles.atajoCount}>
                          Configurar bloques manualmente después de crear
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </section>
            </>
          )}

          {isEdit && salaId && (
            <>
              <div className={styles.tabBanner}>
                <Info size={16} aria-hidden="true" />
                <div>
                  <strong>Horarios y excepciones</strong> &mdash; Defina bloques
                  recurrentes por día de la semana con vigencia. Use{' '}
                  <em>Suspensiones</em> para excepciones puntuales (feriados,
                  capacitaciones).
                </div>
              </div>

              <section className={styles.card}>
                <header className={styles.cardHead}>
                  <div>
                    <h2 className={styles.cardTitle}>
                      <Calendar size={16} /> Horarios recurrentes
                    </h2>
                    <p className={styles.cardSubtitle}>
                      Bloques por día de la semana. Click <strong>+</strong> en cualquier
                      día para agregar uno. Puede agregar varios bloques al mismo día
                      (ej. mañana 08-12, tarde 14-18).
                    </p>
                  </div>
                </header>
                <div className={styles.cardBody}>
                  <HorariosEditor salaId={salaId} />
                </div>
              </section>

              <section className={styles.card}>
                <header className={styles.cardHead}>
                  <div>
                    <h2 className={styles.cardTitle}>
                      <Lock size={16} /> Bloqueos recurrentes
                    </h2>
                    <p className={styles.cardSubtitle}>
                      Rangos que se repiten cada semana y se restan de la disponibilidad
                      (ej. almuerzo de lunes a viernes 12:00-13:00, reunión los lunes 08:00-09:00).
                      Con fecha de inicio, uno o más días y fecha de fin opcional.
                    </p>
                  </div>
                </header>
                <div className={styles.cardBody}>
                  <BloqueosRecurrentesEditor salaId={salaId} />
                </div>
              </section>

              <section className={styles.card}>
                <header className={styles.cardHead}>
                  <div>
                    <h2 className={styles.cardTitle}>
                      <CalendarOff size={16} /> Suspensiones puntuales
                    </h2>
                    <p className={styles.cardSubtitle}>
                      Feriados, capacitaciones u otras excepciones del horario regular.
                    </p>
                  </div>
                </header>
                <div className={styles.cardBody}>
                  <SuspensionesEditor salaId={salaId} />
                </div>
              </section>
            </>
          )}
          </>
          )}

          {/* ── Tab: Capacidad ── */}
          {tab === 'capacidad' && (() => {
            const cuposPorMedicoHora = Math.floor(60 / Math.max(1, form.tiempoEstimadoMin));
            const ejMedicos = 25;
            const capEjemplo = ejMedicos * cuposPorMedicoHora * form.horizonteCapacidadHoras;
            const numInput = (
              field: 'limitePacientes' | 'tiempoEstimadoMin' | 'bufferCierreMins' | 'horizonteCapacidadHoras' | 'ventanaPresenciaMin' | 'bufferSesionMin' | 'timeoutPreVideoMin' | 'graciaReconexionMedicoSeg' | 'graciaFinalizacionMin',
              label: string,
              suffix?: string,
            ) => (
              <div className={styles.capField}>
                <label className={styles.capLabel}>{label}</label>
                <div className={styles.capInputRow}>
                  <input
                    className={styles.capNumInput}
                    type="number"
                    min={RANGOS[field].min}
                    max={RANGOS[field].max}
                    value={String(form[field])}
                    onChange={(e) => updateField(field, clamp(parseInt(e.target.value) || RANGOS[field].min, RANGOS[field]))}
                  />
                  {suffix && <span className={styles.capSuffix}>{suffix}</span>}
                  <span className={styles.capRange}>permitido: {RANGOS[field].min}–{RANGOS[field].max}</span>
                </div>
              </div>
            );
            return (
          <div className={styles.capLayout}>
            {/* Columna de controles */}
            <div className={styles.capControls}>
              <div className={styles.tabBanner}>
                <Info size={16} aria-hidden="true" />
                <div>
                  <strong>Capacidad y admisión</strong> &mdash; define cuántos
                  pacientes puede recibir la sala. Puedes cambiar esta configuración
                  <strong> en cualquier momento</strong>: aplica en caliente (el modo,
                  al instante; los cupos, en ≤1 minuto). El panel de la derecha
                  explica cada opción con ejemplos.
                </div>
              </div>

              {/* 1. Modo de cupos — selector claro */}
              <section className={styles.card}>
                <header className={styles.cardHead}>
                  <h2 className={styles.cardTitle}><Users size={16} /> Modo de cupos</h2>
                </header>
                <div className={styles.cardBody}>
                  <div className={styles.segmented} role="radiogroup" aria-label="Modo de cupos">
                    <button
                      type="button" role="radio" aria-checked={!form.gateDinamico}
                      className={`${styles.segOption} ${!form.gateDinamico ? styles.segOptionActive : ''}`}
                      onClick={() => updateField('gateDinamico', false)}
                    >
                      <span className={styles.segTitle}>Cupos fijos</span>
                      <span className={styles.segDesc}>La sala admite hasta un número fijo de pacientes en cola.</span>
                    </button>
                    <button
                      type="button" role="radio" aria-checked={form.gateDinamico}
                      className={`${styles.segOption} ${form.gateDinamico ? styles.segOptionActive : ''}`}
                      onClick={() => updateField('gateDinamico', true)}
                    >
                      <span className={styles.segTitle}>Cupos dinámicos</span>
                      <span className={styles.segDesc}>La sala admite según cuántos médicos tiene y cuánto atiende cada uno.</span>
                    </button>
                  </div>
                </div>
              </section>

              {/* 2. Parámetros */}
              <section className={styles.card}>
                <header className={styles.cardHead}>
                  <h2 className={styles.cardTitle}>Parámetros</h2>
                </header>
                <div className={styles.cardBody}>
                  {numInput('limitePacientes', 'Tope máximo de pacientes en cola')}
                  {numInput('tiempoEstimadoMin', 'Tiempo por consulta', 'min')}

                  {form.gateDinamico && (
                    <>
                      {numInput('horizonteCapacidadHoras', 'Horas de atención a considerar', 'h')}

                      <label className={styles.capToggle}>
                        <input
                          type="checkbox"
                          checked={form.descontarDesconectados}
                          onChange={(e) => updateField('descontarDesconectados', e.target.checked)}
                        />
                        <div>
                          <div className={styles.capToggleTitle}>Descontar médicos desconectados</div>
                          <div className={styles.capToggleDesc}>
                            Si se activa, sólo cuentan los médicos con señal reciente.
                            Si se deja apagado (recomendado), cuentan todos los médicos
                            asignados a la sala — más estable y considera a los que aún
                            no se conectan al inicio del turno.
                          </div>
                        </div>
                      </label>
                      {form.descontarDesconectados &&
                        numInput('ventanaPresenciaMin', 'Tolerancia de desconexión', 'min')}
                    </>
                  )}

                  {numInput('bufferCierreMins', 'Buffer de cierre (antes del fin)', 'min')}
                </div>
              </section>

              {/* 3. Tiempos de la sesión de teleconsulta */}
              <section className={styles.card}>
                <header className={styles.cardHead}>
                  <h2 className={styles.cardTitle}>Tiempos de la teleconsulta</h2>
                </header>
                <div className={styles.cardBody}>
                  {numInput('bufferSesionMin', 'Margen extra de la sesión de video', 'min')}
                  {numInput('timeoutPreVideoMin', 'Tiempo para iniciar el video tras tomar', 'min')}
                  {numInput('graciaReconexionMedicoSeg', 'Gracia si el médico pierde conexión', 'seg')}
                  {numInput('graciaFinalizacionMin', 'Tiempo para redactar la nota tras colgar', 'min')}
                  <p className={styles.helpNote} style={{ marginTop: 8 }}>
                    La sesión de video dura <strong>{form.tiempoEstimadoMin} + {form.bufferSesionMin} = {form.tiempoEstimadoMin + form.bufferSesionMin} min</strong>,
                    y el reloj sólo empieza cuando el médico <strong>inicia la videollamada</strong>.
                    Si toma al paciente pero no inicia en {form.timeoutPreVideoMin} min, el paciente
                    vuelve a la cola. Si el médico pierde conexión durante la llamada, se esperan
                    {' '}{form.graciaReconexionMedicoSeg} s antes de devolver al paciente a la cola.
                  </p>

                  {/* Este parámetro se explica aparte y con detalle: es el que
                      decide si una atención real queda archivada como
                      "incompleta", y eso afecta el expediente y los reportes. */}
                  <div className={styles.helpNote} style={{ marginTop: 12, borderLeft: '3px solid var(--color-primary, #1A8048)', paddingLeft: 12 }}>
                    <strong>Tiempo para redactar la nota tras colgar ({form.graciaFinalizacionMin} min)</strong>
                    <p style={{ margin: '6px 0 0' }}>
                      Cuando el médico <strong>cuelga la videollamada</strong>, la consulta no se
                      cierra: el médico se queda en pantalla revisando y firmando la nota médica.
                      Este valor es el tiempo máximo que el sistema espera esa firma.
                    </p>
                    <p style={{ margin: '6px 0 0' }}>
                      Si el médico <strong>sigue trabajando en la pantalla</strong>, la consulta NO
                      se cierra aunque pase este tiempo — el sistema detecta que está activo y lo
                      deja terminar. El límite sólo actúa cuando el médico <strong>cerró la
                      pestaña o se desconectó</strong> sin firmar.
                    </p>
                    <p style={{ margin: '6px 0 0' }}>
                      Al agotarse, el sistema cierra la consulta solo, consolida la nota con lo que
                      la IA alcanzó a capturar y la marca como <strong>«tiempo excedido»</strong>,
                      que en los reportes cuenta como atención <em>incompleta</em>.
                    </p>
                  </div>
                </div>
              </section>
            </div>

            {/* Columna de ayuda — punto por punto */}
            <aside className={styles.capHelp}>
              <div className={styles.helpGeneral}>
                <div className={styles.helpGeneralTitle}>¿Cómo se admite a los pacientes?</div>
                <p>
                  Cuando un paciente pide entrar a la cola, el sistema compara los
                  pacientes que ya esperan contra la <strong>capacidad</strong> de la
                  sala. Si hay espacio, entra; si no, se le pide intentar más tarde.
                </p>
                <p>
                  {form.gateDinamico ? (
                    <>Estás en <strong>Cupos dinámicos</strong>: la capacidad se calcula
                    con los médicos de la sala. El «tope máximo» sigue siendo el límite
                    absoluto que nunca se supera.</>
                  ) : (
                    <>Estás en <strong>Cupos fijos</strong>: la sala admite hasta el
                    «tope máximo de pacientes» que definas, sin importar cuántos médicos
                    haya conectados.</>
                  )}
                </p>
                <p className={styles.helpNote}>
                  Los valores se recalculan cada ~1 minuto y todos se ajustan
                  automáticamente a un rango seguro: aunque escribas un número extremo,
                  el sistema lo recorta para no afectar la aplicación.
                </p>
              </div>

              <HelpItem n={1} titulo="Modo de cupos">
                <strong>Fijo</strong>: simple y predecible; siempre admite hasta el tope.
                Útil si la cantidad de médicos varía poco o prefieres un número claro.
                <br /><strong>Dinámico</strong>: la capacidad sube o baja con los médicos
                de la sala. Ideal cuando quieres aprovechar mejor la disponibilidad real.
              </HelpItem>

              <HelpItem n={2} titulo="Tope máximo de pacientes en cola">
                Límite de seguridad que <em>nunca</em> se supera, en cualquier modo. En
                cupos fijos es la capacidad; en dinámicos es el techo absoluto.
                <br /><em>Ej.:</em> tope 200 → aunque el cálculo dinámico dé 300, nunca
                entran más de 200 en cola.
              </HelpItem>

              <HelpItem n={3} titulo="Tiempo por consulta">
                Cuánto dura en promedio cada atención. Define cuántos pacientes atiende
                un médico por hora: <strong>60 ÷ {form.tiempoEstimadoMin} = {cuposPorMedicoHora}
                {' '}por hora</strong>.
              </HelpItem>

              {form.gateDinamico && (
                <>
                  <HelpItem n={4} titulo="Horas de atención a considerar">
                    Cuántas horas de trabajo por delante se permiten en cola. A más horas,
                    más gente puede esperar (y esperas más largas).
                    <br /><em>Ej.:</em> {ejMedicos} médicos × {cuposPorMedicoHora}/h ×
                    {' '}{form.horizonteCapacidadHoras} h = <strong>{capEjemplo} cupos</strong>.
                  </HelpItem>

                  <HelpItem n={5} titulo="Descontar médicos desconectados">
                    <strong>Apagado (recomendado):</strong> cuentan todos los médicos
                    asignados a la sala, aunque alguno no se haya conectado aún. Es más
                    estable y evita perder cupos al inicio del turno. Para bajar la
                    capacidad, el administrador quita médicos de la sala.
                    <br /><strong>Encendido:</strong> sólo cuentan los que tienen señal
                    reciente. Más estricto, pero puede rechazar pacientes durante caídas
                    breves de conexión.
                  </HelpItem>

                  {form.descontarDesconectados && (
                    <HelpItem n={6} titulo="Tolerancia de desconexión">
                      Minutos que un médico puede estar sin señal antes de dejar de contar.
                      Un valor alto es más estable (tolera reconexiones); uno bajo reacciona
                      más rápido pero puede quitar cupos por microcortes.
                      <br /><em>Ej.:</em> {form.ventanaPresenciaMin} min → si un médico se
                      desconecta, se sigue contando hasta {form.ventanaPresenciaMin} minutos.
                    </HelpItem>
                  )}
                </>
              )}

              <HelpItem n={form.gateDinamico ? (form.descontarDesconectados ? 7 : 6) : 4} titulo="Buffer de cierre">
                Minutos antes del cierre en que se deja de aceptar pacientes, para que
                los médicos terminen sin horas extra.
                <br /><em>Ej.:</em> cierre 17:00 y buffer 15 → la cola cierra 16:45.
              </HelpItem>
            </aside>
          </div>
            );
          })()}

          {/* ── Tab: Médicos & Asignación ── */}
          {tab === 'medicos' && (
          <>
          <div className={styles.tabBanner}>
            <Info size={16} aria-hidden="true" />
            <div>
              <strong>Médicos y asignación</strong> &mdash; Asigne uno o más médicos
              a esta sala con su rol (titular/suplente) y elija cómo se distribuyen
              los pacientes. <em>Manual</em> deja al médico elegir;
              <em> Automático</em> asigna al terminar cada consulta;{' '}
              <em>Híbrido</em> es la mezcla recomendada (el sistema asigna pero el
              médico puede rechazar).
            </div>
          </div>
          <section className={styles.card}>
            <header className={styles.cardHead}>
              <div>
                <h2 className={styles.cardTitle}>
                  <Settings size={16} /> Modo de asignación
                </h2>
                <p className={styles.cardSubtitle}>
                  Cómo se asignan los pacientes a los médicos disponibles.
                </p>
              </div>
            </header>
            <div className={styles.cardBody}>
              <div className={styles.modeGroup} role="radiogroup" aria-label="Modo de asignación">
                {MODOS_ASIGNACION.map((m) => {
                  const active = form.modoAsignacion === m.value;
                  return (
                    <label
                      key={m.value}
                      className={`${styles.modeOption} ${active ? styles.modeOptionActive : ''}`}
                    >
                      <input
                        type="radio"
                        name="modoAsignacion"
                        value={m.value}
                        checked={active}
                        onChange={() => updateField('modoAsignacion', m.value)}
                      />
                      <div className={styles.modeOptionBody}>
                        <div className={styles.modeOptionTitle}>{m.label}</div>
                        <div className={styles.modeOptionDesc}>{m.description}</div>
                      </div>
                    </label>
                  );
                })}
              </div>

              {/* COLA-02: oferta Híbrida (opt-in) — sólo en modo Híbrido.
                  Checkbox (no radio: es independiente del modo, no una opción
                  excluyente) — pero comparte el estilo de tarjeta .modeOption
                  con el input nativo oculto y el resaltado por selección,
                  igual que el radiogroup de arriba, para que se vea como un
                  control coherente en vez de un checkbox suelto sin resaltar. */}
              {form.modoAsignacion === 'hibrido' && (
                <label
                  className={`${styles.modeOption} ${form.ofertaHibridaActiva ? styles.modeOptionActive : ''}`}
                  style={{ marginTop: 16 }}
                >
                  <input
                    type="checkbox"
                    checked={form.ofertaHibridaActiva}
                    onChange={(e) => updateField('ofertaHibridaActiva', e.target.checked)}
                  />
                  <div className={styles.modeOptionBody}>
                    <div className={styles.modeOptionTitle}>Ofrecer antes de asignar (aceptar/rechazar)</div>
                    <div className={styles.modeOptionDesc}>
                      Cuando está activo, el sistema OFRECE el paciente al médico y éste
                      acepta o rechaza; si expira, se reasigna a otro. Con esto apagado,
                      el modo Híbrido asigna directo (como Automático). Funcionalidad nueva
                      — actívela primero en una sala de prueba.
                    </div>
                  </div>
                </label>
              )}
            </div>
          </section>

          {/* MedicosAsignadosEditor + RoutingPolicy — solo en modo edit */}
          {isEdit && salaId && (
            <>
              <section className={styles.card}>
                <header className={styles.cardHead}>
                  <div>
                    <h2 className={styles.cardTitle}>Médicos asignados</h2>
                    <p className={styles.cardSubtitle}>
                      Profesionales autorizados para atender en esta sala. Use el
                      botón <em>Asignar médico</em> para añadir nuevos. Cada médico
                      puede heredar el horario de la sala o tener uno propio.
                    </p>
                  </div>
                </header>
                <div className={styles.cardBody}>
                  <MedicosAsignadosEditor salaId={salaId} />
                </div>
              </section>

              <section className={styles.card}>
                <header className={styles.cardHead}>
                  <div>
                    <h2 className={styles.cardTitle}>Política de asignación (M8)</h2>
                    <p className={styles.cardSubtitle}>
                      Cómo el sistema elige al siguiente médico cuando llegan
                      pacientes. Configurable por sala — cada tipo de atención
                      puede tener su propia estrategia.
                    </p>
                  </div>
                </header>
                <div className={styles.cardBody}>
                  <RoutingPolicyEditor salaId={salaId} />
                </div>
              </section>
            </>
          )}
          </>
          )}

          {/* ── Tab: General — Opciones e instrucciones ── */}
          {tab === 'general' && (
          <section className={styles.card}>
            <header className={styles.cardHead}>
              <div>
                <h2 className={styles.cardTitle}>
                  <Settings size={16} /> Opciones e instrucciones
                </h2>
                <p className={styles.cardSubtitle}>
                  Configuración avanzada y mensaje para el paciente.
                </p>
              </div>
            </header>
            <div className={styles.cardBody}>
              <div className={styles.optionsList}>
                <label className={styles.optionRow}>
                  <input
                    type="checkbox"
                    checked={form.activa}
                    onChange={(e) => updateField('activa', e.target.checked)}
                  />
                  <div>
                    <div className={styles.optionTitle}>Sala activa</div>
                    <div className={styles.optionHint}>
                      Si está desactivada, no será visible para los pacientes.
                    </div>
                  </div>
                </label>

                <label className={styles.optionRow}>
                  <input
                    type="checkbox"
                    checked={form.requiereAprobacion}
                    onChange={(e) => updateField('requiereAprobacion', e.target.checked)}
                  />
                  <div>
                    <div className={styles.optionTitle}>Requiere aprobación del médico</div>
                    <div className={styles.optionHint}>
                      El médico debe aprobar manualmente cada solicitud antes de
                      que el paciente entre a la cola.
                    </div>
                  </div>
                </label>
              </div>

              <div className={styles.fullWidth}>
                <label className={styles.label}>Instrucciones para el paciente</label>
                <textarea
                  className={styles.textarea}
                  value={form.instrucciones}
                  onChange={(e) => updateField('instrucciones', e.target.value)}
                  placeholder="Ej. Tenga a mano su cédula y recetas actuales antes de iniciar la consulta."
                  rows={3}
                />
              </div>
            </div>
          </section>

          )}{/* end tab=general (Opciones) */}

          {/* ── Tab: Mensajes al paciente ── */}
          {tab === 'mensajes' && (
          <>
          <div className={styles.tabBanner}>
            <Info size={16} aria-hidden="true" />
            <div>
              <strong>Mensajes al paciente</strong> &mdash; Personalice el texto que
              recibe el paciente cuando <em>no</em> puede entrar a la cola (sala llena,
              cerrada, sin médicos…). El objetivo es que el mensaje sea claro y lo
              anime a reintentar, para aprovechar los cupos que se liberan durante el
              día. Deje un campo vacío para usar el texto por defecto. Las
              <strong> variables entre llaves</strong> (p. ej. <code>{'{tiempo}'}</code>)
              las reemplaza el sistema automáticamente.
            </div>
          </div>
          <section className={styles.card}>
            <div className={styles.cardBody}>
              <div className={styles.msgList}>
                {MENSAJES.map((m) => {
                  const valor = form.mensajesPaciente[m.key] ?? '';
                  const personalizado = valor.trim().length > 0;
                  return (
                    <div key={m.key} className={styles.msgItem}>
                      <div className={styles.msgHead}>
                        <div>
                          <div className={styles.msgLabel}>{m.label}</div>
                          <div className={styles.msgCuando}>{m.cuando}</div>
                        </div>
                        <span className={`${styles.msgBadge} ${personalizado ? styles.msgBadgeCustom : ''}`}>
                          {personalizado ? 'Personalizado' : 'Por defecto'}
                        </span>
                      </div>
                      <textarea
                        className={styles.msgTextarea}
                        value={valor}
                        maxLength={400}
                        rows={2}
                        placeholder={m.default}
                        onChange={(e) => updateMensaje(m.key, e.target.value)}
                      />
                      <div className={styles.msgFoot}>
                        <div className={styles.msgVars}>
                          {m.variables.length > 0 ? (
                            <>
                              <span>Variables:</span>
                              {m.variables.map((v) => (
                                <code key={v.token} title={v.desc}>{v.token}</code>
                              ))}
                            </>
                          ) : (
                            <span className={styles.msgVarsNone}>Sin variables</span>
                          )}
                        </div>
                        {personalizado && (
                          <button
                            type="button"
                            className={styles.msgReset}
                            onClick={() => updateMensaje(m.key, '')}
                          >
                            <RotateCcw size={12} /> Restaurar por defecto
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
          </>
          )}

          {/* ── Tab: Vista previa ── */}
          {tab === 'vista-previa' && isEdit && salaId && (
            <section className={styles.card}>
              <div className={styles.cardBody}>
                <VistaPreviaCapacidad salaId={salaId} />
              </div>
            </section>
          )}

          {/* ── Sticky action bar ── */}
          <div className={styles.actionBar}>
            <span className={styles.actionHint}>
              {isEdit
                ? 'Los cambios se aplican inmediatamente.'
                : 'La sala se creará con la configuración indicada.'}
            </span>
            <div className={styles.actionBtns}>
              <BadgeCambiosSinGuardar visible={hayCambios} />
              <Button variant="secondary" type="button" onClick={handleCancel}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                type="submit"
                loading={saving}
                disabled={!canSubmit}
              >
                <Save size={14} />
                {isEdit ? 'Guardar cambios' : 'Crear sala'}
              </Button>
            </div>
          </div>
        </form>
      </div>

    </MainLayout>
  );
}
